import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import RequestsPage from './pages/RequestsPage';
import HRDashboardPage from './pages/HRDashboardPage';
import AttendancePage from './pages/AttendancePage';
import PayslipsPage from './pages/PayslipsPage';
import ProductionDashboardPage from './pages/ProductionDashboardPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import ErpPage from './pages/ErpPage';
import ChangePasswordModal from './components/ChangePasswordModal';
import { Toaster } from './components/ui/sonner';

const ROLE_HOME_PATH = {
  hr: '/hr',
  production: '/production',
  erp: '/erp',
};

const roleHomePath = (role) => ROLE_HOME_PATH[role] || '/';

const ProtectedRoute = ({ children, requireRole }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen w-screen flex items-center justify-center bg-white"><div className="text-slate-500">Caricamento...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Dual-role support: allow access if user has the explicit access flag
  // (e.g., HR admin with production_access=true can access /production).
  // 'employee' routes are open to any authenticated user.
  const hasAccess =
    !requireRole ||
    requireRole === 'employee' ||
    user.role === requireRole ||
    (requireRole === 'production' && user.production_access) ||
    (requireRole === 'erp' && user.erp_access) ||
    (requireRole === 'hr' && user.hr_access);
  if (!hasAccess) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }
  return (
    <>
      <ChangePasswordModal />
      {children}
    </>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={roleHomePath(user.role)} replace />;
  return children;
};

const ErpRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen w-screen flex items-center justify-center bg-white"><div className="text-slate-500">Caricamento...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'erp' && !user.erp_access) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }
  return <><ChangePasswordModal />{children}</>;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/" element={<ProtectedRoute requireRole="employee"><ChatPage /></ProtectedRoute>} />
            <Route path="/chat/:chatId" element={<ProtectedRoute requireRole="employee"><ChatPage /></ProtectedRoute>} />
            <Route path="/requests" element={<ProtectedRoute requireRole="employee"><RequestsPage /></ProtectedRoute>} />
            <Route path="/attendance" element={<ProtectedRoute requireRole="employee"><AttendancePage /></ProtectedRoute>} />
            <Route path="/payslips" element={<ProtectedRoute requireRole="employee"><PayslipsPage /></ProtectedRoute>} />
            <Route path="/hr" element={<ProtectedRoute requireRole="hr"><HRDashboardPage /></ProtectedRoute>} />
            <Route path="/production" element={<ProtectedRoute requireRole="production"><ProductionDashboardPage /></ProtectedRoute>} />
            <Route path="/production/customers/:customerId" element={<ProtectedRoute requireRole="production"><CustomerDetailPage /></ProtectedRoute>} />
            <Route path="/erp" element={<ErpRoute><ErpPage /></ErpRoute>} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
